import React, { useState, useEffect, useMemo } from 'react';
import { FaCalendar, FaCheckCircle, FaClock, FaExclamationTriangle, FaSpinner, FaSave, FaUndo } from 'react-icons/fa';
import { API_ENDPOINTS } from '../../config/api';

// ─── AU BAS quarter math ─────────────────────────────────────────────────
// FY in AU starts July 1. "FY2026" = July 2025 - June 2026.
// Q1 Jul-Sep · Q2 Oct-Dec · Q3 Jan-Mar · Q4 Apr-Jun
// Self-lodged deadlines: Q1 28-Oct, Q2 28-Feb (xmas ext.), Q3 28-Apr, Q4 28-Jul.

const QUARTER_OF_MONTH = {
    7: 1, 8: 1, 9: 1,
    10: 2, 11: 2, 12: 2,
    1: 3, 2: 3, 3: 3,
    4: 4, 5: 4, 6: 4,
};

const quarterFromDate = (date) => {
    const m = date.getMonth() + 1; // 1-12
    const y = date.getFullYear();
    const q = QUARTER_OF_MONTH[m];
    // Fiscal year ending June 30 next year if we're in Jul-Dec; same year if Jan-Jun.
    const fy = (m >= 7) ? y + 1 : y;
    return { fy, q };
};

const quarterMeta = (fy, q) => {
    // FY2026, Q1 → start = 2025-07-01, end = 2025-09-30, due = 2025-10-28
    const startYear = fy - 1; // calendar year that contains Jul-Dec of FY
    const periods = {
        1: { startMonth: 7, endMonth: 9, startCY: startYear, endCY: startYear, dueMonth: 10, dueDay: 28, dueCY: startYear },
        2: { startMonth: 10, endMonth: 12, startCY: startYear, endCY: startYear, dueMonth: 2, dueDay: 28, dueCY: fy },
        3: { startMonth: 1, endMonth: 3, startCY: fy, endCY: fy, dueMonth: 4, dueDay: 28, dueCY: fy },
        4: { startMonth: 4, endMonth: 6, startCY: fy, endCY: fy, dueMonth: 7, dueDay: 28, dueCY: fy },
    };
    const p = periods[q];
    const startDate = new Date(p.startCY, p.startMonth - 1, 1);
    const endDate = new Date(p.endCY, p.endMonth, 0); // last day of endMonth
    const dueDate = new Date(p.dueCY, p.dueMonth - 1, p.dueDay);
    const monthKeys = [];
    let cur = new Date(startDate);
    while (cur <= endDate) {
        monthKeys.push(`${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}`);
        cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
    }
    return {
        id: `FY${fy}-Q${q}`,
        label: `Q${q} FY${fy}`,
        periodLabel: `${startDate.toLocaleDateString('en-AU', { month: 'short' })} – ${endDate.toLocaleDateString('en-AU', { month: 'short', year: 'numeric' })}`,
        startDate,
        endDate,
        dueDate,
        monthKeys,
    };
};

// Build a list of last 6 quarters (5 past + current) for the dropdown.
const buildQuarterList = (now = new Date()) => {
    const { fy, q } = quarterFromDate(now);
    const list = [];
    let curFy = fy;
    let curQ = q;
    for (let i = 0; i < 6; i++) {
        list.push(quarterMeta(curFy, curQ));
        curQ -= 1;
        if (curQ < 1) { curQ = 4; curFy -= 1; }
    }
    return list;
};

const fmtAud = (n) => new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(n || 0);

const BasQuarterPanel = ({ gstCollectedByMonth, gstPaidByMonth }) => {
    const quarters = useMemo(() => buildQuarterList(new Date()), []);
    const [selectedId, setSelectedId] = useState(quarters[0].id); // current quarter default
    const selected = quarters.find(q => q.id === selectedId) || quarters[0];

    const [lodgements, setLodgements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showLodgeModal, setShowLodgeModal] = useState(false);

    // Compute quarter-scoped totals from the monthly maps.
    const totals = useMemo(() => {
        const gstCollected = selected.monthKeys.reduce((s, k) => s + (gstCollectedByMonth?.[k] || 0), 0);
        const gstPaid = selected.monthKeys.reduce((s, k) => s + (gstPaidByMonth?.[k] || 0), 0);
        return {
            gstCollected,
            gstPaid,
            netGst: gstCollected - gstPaid,
        };
    }, [selected, gstCollectedByMonth, gstPaidByMonth]);

    // Days-to-deadline status. Negative = overdue.
    const daysToDeadline = useMemo(() => {
        const now = new Date();
        const ms = selected.dueDate.getTime() - now.getTime();
        return Math.ceil(ms / (1000 * 60 * 60 * 24));
    }, [selected]);

    const lodgement = lodgements.find(l => l.quarterId === selected.id);

    useEffect(() => {
        loadLodgements();
    }, []);

    const loadLodgements = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('adminToken');
            const r = await fetch(API_ENDPOINTS.ADMIN_GET_CUSTOMERS, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: token ? `Bearer ${token}` : '',
                },
                body: JSON.stringify({ action: 'listBasLodgements' }),
            });
            const data = await r.json();
            if (data.success) setLodgements(data.lodgements || []);
        } catch (e) {
            console.error('Error loading BAS lodgements:', e);
        } finally {
            setLoading(false);
        }
    };

    const handleLodged = async (formData) => {
        const token = localStorage.getItem('adminToken');
        const r = await fetch(API_ENDPOINTS.ADMIN_GET_CUSTOMERS, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: token ? `Bearer ${token}` : '',
            },
            body: JSON.stringify({
                action: 'saveBasLodgement',
                quarterId: selected.id,
                ...formData,
            }),
        });
        const data = await r.json();
        if (!data.success) throw new Error(data.error || 'Save failed');
        await loadLodgements();
    };

    const handleUnlodge = async () => {
        if (!confirm(`Remove the lodgement record for ${selected.label}? This doesn't undo anything on the ATO side — just clears it from this page.`)) return;
        const token = localStorage.getItem('adminToken');
        await fetch(API_ENDPOINTS.ADMIN_GET_CUSTOMERS, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: token ? `Bearer ${token}` : '',
            },
            body: JSON.stringify({ action: 'deleteBasLodgement', quarterId: selected.id }),
        });
        await loadLodgements();
    };

    // Deadline badge style
    const deadlineClass =
        daysToDeadline < 0 ? 'text-[#fb4934] bg-[#3c1f1f] border-[#cc241d]' :
        daysToDeadline <= 14 ? 'text-[#fb4934] bg-[#3c2f1f] border-[#d79921]' :
        daysToDeadline <= 30 ? 'text-[#fabd2f] bg-[#3c3320] border-[#d79921]' :
        'text-[#928374] bg-[#3c3836] border-[#504945]';

    return (
        <div className="bg-[#282828] rounded-xl shadow-sm border border-[#3c3836] p-6">
            <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
                <div>
                    <h2 className="text-xl font-bold text-[#ebdbb2]">BAS Quarters</h2>
                    <p className="text-sm text-[#928374]">Per-quarter GST totals + lodgement records. AU FY runs Jul–Jun.</p>
                </div>
                <select
                    value={selectedId}
                    onChange={(e) => setSelectedId(e.target.value)}
                    className="px-4 py-2 bg-[#3c3836] border border-[#504945] rounded-lg text-[#ebdbb2] focus:ring-2 focus:ring-[#fabd2f] focus:border-transparent"
                >
                    {quarters.map(q => (
                        <option key={q.id} value={q.id}>
                            {q.label} · {q.periodLabel}
                        </option>
                    ))}
                </select>
            </div>

            {/* Quarter header — period + deadline */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="bg-[#3c3836] border border-[#504945] rounded-lg p-4">
                    <p className="text-xs uppercase font-semibold text-[#928374] mb-1 flex items-center gap-1">
                        <FaCalendar /> Period
                    </p>
                    <p className="text-lg font-bold text-[#ebdbb2]">{selected.periodLabel}</p>
                    <p className="text-xs text-[#928374] mt-1">
                        {selected.startDate.toLocaleDateString('en-AU')} → {selected.endDate.toLocaleDateString('en-AU')}
                    </p>
                </div>
                <div className={`border rounded-lg p-4 ${deadlineClass}`}>
                    <p className="text-xs uppercase font-semibold mb-1 flex items-center gap-1">
                        <FaClock /> Deadline {lodgement && <span className="ml-1 text-[#b8bb26]">(lodged)</span>}
                    </p>
                    <p className="text-lg font-bold">{selected.dueDate.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                    <p className="text-xs mt-1">
                        {lodgement
                            ? `✓ Lodged ${new Date(lodgement.lodgedAt).toLocaleDateString('en-AU')}`
                            : daysToDeadline < 0
                                ? `⚠ ${Math.abs(daysToDeadline)} days OVERDUE`
                                : `${daysToDeadline} day${daysToDeadline === 1 ? '' : 's'} remaining`}
                    </p>
                </div>
            </div>

            {/* Scoped GST totals */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="bg-[#3c3836] border border-[#458588] rounded-lg p-4">
                    <p className="text-xs uppercase font-semibold text-[#83a598]">1A · GST Collected</p>
                    <p className="text-2xl font-bold text-[#b8bb26] mt-1">{fmtAud(totals.gstCollected)}</p>
                </div>
                <div className="bg-[#3c3836] border border-[#d79921] rounded-lg p-4">
                    <p className="text-xs uppercase font-semibold text-[#fabd2f]">1B · GST Paid</p>
                    <p className="text-2xl font-bold text-[#fabd2f] mt-1">{fmtAud(totals.gstPaid)}</p>
                </div>
                <div className="bg-[#3c3836] border border-[#cc241d] rounded-lg p-4">
                    <p className="text-xs uppercase font-semibold text-[#fb4934]">Net GST</p>
                    <p className={`text-2xl font-bold mt-1 ${totals.netGst >= 0 ? 'text-[#fb4934]' : 'text-[#b8bb26]'}`}>{fmtAud(totals.netGst)}</p>
                    <p className="text-xs text-[#928374] mt-0.5">{totals.netGst >= 0 ? 'Owed to ATO' : 'Refundable'}</p>
                </div>
            </div>

            {/* Action: Mark as Lodged */}
            <div className="flex items-center justify-between gap-3 pt-4 border-t border-[#3c3836]">
                <div className="text-sm text-[#928374]">
                    {lodgement
                        ? <>Recorded lodgement: <span className="text-[#ebdbb2] font-medium">{fmtAud(lodgement.netGst)}</span> net{lodgement.paymentReference ? ` · ref ${lodgement.paymentReference}` : ''}</>
                        : 'After you lodge with the ATO, record it here for the historical trail.'}
                </div>
                <div className="flex gap-2">
                    {lodgement && (
                        <button
                            onClick={handleUnlodge}
                            className="px-3 py-2 text-xs border border-[#504945] text-[#928374] rounded hover:bg-[#3c3836] inline-flex items-center gap-1"
                        >
                            <FaUndo /> Clear
                        </button>
                    )}
                    <button
                        onClick={() => setShowLodgeModal(true)}
                        className="px-4 py-2 bg-[#b8bb26] text-[#1d2021] rounded-lg font-medium hover:bg-[#98971a] inline-flex items-center gap-2"
                    >
                        <FaCheckCircle /> {lodgement ? 'Update Lodgement' : 'Mark as Lodged'}
                    </button>
                </div>
            </div>

            {/* Past quarters table */}
            <div className="mt-6 pt-4 border-t border-[#3c3836]">
                <h3 className="text-sm font-semibold text-[#bdae93] mb-2">Lodgement History</h3>
                {loading ? (
                    <p className="text-sm text-[#928374]"><FaSpinner className="inline animate-spin mr-2" /> Loading…</p>
                ) : lodgements.length === 0 ? (
                    <p className="text-sm text-[#928374]">No quarters recorded yet. Mark a quarter as Lodged after BAS submission.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-xs uppercase text-[#928374] border-b border-[#3c3836]">
                                    <th className="px-3 py-2">Quarter</th>
                                    <th className="px-3 py-2 text-right">GST Collected (1A)</th>
                                    <th className="px-3 py-2 text-right">GST Paid (1B)</th>
                                    <th className="px-3 py-2 text-right">Net GST</th>
                                    <th className="px-3 py-2">Lodged</th>
                                    <th className="px-3 py-2">Ref</th>
                                </tr>
                            </thead>
                            <tbody>
                                {[...lodgements].sort((a, b) => b.quarterId.localeCompare(a.quarterId)).map(l => (
                                    <tr key={l.quarterId} className="border-b border-[#3c3836] text-[#ebdbb2]">
                                        <td className="px-3 py-2 font-mono">{l.quarterId}</td>
                                        <td className="px-3 py-2 text-right">{fmtAud(l.gstCollected)}</td>
                                        <td className="px-3 py-2 text-right">{fmtAud(l.gstPaid)}</td>
                                        <td className={`px-3 py-2 text-right font-medium ${l.netGst >= 0 ? 'text-[#fb4934]' : 'text-[#b8bb26]'}`}>{fmtAud(l.netGst)}</td>
                                        <td className="px-3 py-2 text-[#928374] text-xs">{l.lodgedAt ? new Date(l.lodgedAt).toLocaleDateString('en-AU') : '—'}</td>
                                        <td className="px-3 py-2 text-[#928374] text-xs font-mono">{l.paymentReference || '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {showLodgeModal && (
                <LodgeModal
                    quarter={selected}
                    initialValues={lodgement || totals}
                    onClose={() => setShowLodgeModal(false)}
                    onSubmit={async (formData) => {
                        await handleLodged(formData);
                        setShowLodgeModal(false);
                    }}
                />
            )}
        </div>
    );
};

// ─── Mark-as-Lodged modal ───────────────────────────────────────────────
const LodgeModal = ({ quarter, initialValues, onClose, onSubmit }) => {
    const [gstCollected, setGstCollected] = useState(initialValues.gstCollected?.toFixed(2) ?? '0.00');
    const [gstPaid, setGstPaid] = useState(initialValues.gstPaid?.toFixed(2) ?? '0.00');
    const [paymentReference, setPaymentReference] = useState(initialValues.paymentReference || '');
    const [lodgedAt, setLodgedAt] = useState((initialValues.lodgedAt || new Date().toISOString()).split('T')[0]);
    const [notes, setNotes] = useState(initialValues.notes || '');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    const netGst = (parseFloat(gstCollected) || 0) - (parseFloat(gstPaid) || 0);

    const submit = async () => {
        if (submitting) return;
        setSubmitting(true);
        setError('');
        try {
            await onSubmit({
                gstCollected: parseFloat(gstCollected) || 0,
                gstPaid: parseFloat(gstPaid) || 0,
                netGst,
                paymentReference: paymentReference.trim() || null,
                lodgedAt: new Date(lodgedAt).toISOString(),
                notes: notes.trim() || null,
            });
        } catch (e) {
            setError(e.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-[#282828] border border-[#3c3836] rounded-xl max-w-lg w-full p-6">
                <h2 className="text-xl font-bold text-[#ebdbb2] mb-1">Record BAS Lodgement</h2>
                <p className="text-sm text-[#928374] mb-4">{quarter.label} · {quarter.periodLabel}</p>

                <div className="space-y-3 text-sm">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs text-[#bdae93] mb-1">GST Collected (1A)</label>
                            <input type="number" step="0.01" value={gstCollected} onChange={(e) => setGstCollected(e.target.value)}
                                className="w-full px-3 py-2 bg-[#1d2021] border border-[#504945] rounded text-[#ebdbb2] font-mono" />
                        </div>
                        <div>
                            <label className="block text-xs text-[#bdae93] mb-1">GST Paid (1B)</label>
                            <input type="number" step="0.01" value={gstPaid} onChange={(e) => setGstPaid(e.target.value)}
                                className="w-full px-3 py-2 bg-[#1d2021] border border-[#504945] rounded text-[#ebdbb2] font-mono" />
                        </div>
                    </div>
                    <div className="bg-[#3c3836] rounded p-3 flex items-center justify-between">
                        <span className="text-xs text-[#bdae93] uppercase">Net GST {netGst >= 0 ? 'payable' : 'refundable'}</span>
                        <span className={`font-bold ${netGst >= 0 ? 'text-[#fb4934]' : 'text-[#b8bb26]'}`}>{fmtAud(Math.abs(netGst))}</span>
                    </div>
                    <div>
                        <label className="block text-xs text-[#bdae93] mb-1">Lodgement Date</label>
                        <input type="date" value={lodgedAt} onChange={(e) => setLodgedAt(e.target.value)}
                            className="w-full px-3 py-2 bg-[#1d2021] border border-[#504945] rounded text-[#ebdbb2]" />
                    </div>
                    <div>
                        <label className="block text-xs text-[#bdae93] mb-1">ATO Payment Reference (optional)</label>
                        <input type="text" value={paymentReference} onChange={(e) => setPaymentReference(e.target.value)}
                            placeholder="e.g. EFT ref, BAS DLN…"
                            className="w-full px-3 py-2 bg-[#1d2021] border border-[#504945] rounded text-[#ebdbb2] font-mono" />
                    </div>
                    <div>
                        <label className="block text-xs text-[#bdae93] mb-1">Notes (optional)</label>
                        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
                            placeholder="Discrepancies, manual adjustments, etc."
                            className="w-full px-3 py-2 bg-[#1d2021] border border-[#504945] rounded text-[#ebdbb2]" />
                    </div>
                    {error && <p className="text-sm text-[#fb4934]">{error}</p>}
                </div>

                <div className="flex gap-2 justify-end mt-5">
                    <button onClick={onClose} disabled={submitting}
                        className="px-4 py-2 border border-[#504945] text-[#bdae93] rounded hover:bg-[#3c3836]">
                        Cancel
                    </button>
                    <button onClick={submit} disabled={submitting}
                        className="px-4 py-2 bg-[#b8bb26] text-[#1d2021] rounded font-medium hover:bg-[#98971a] inline-flex items-center gap-2 disabled:opacity-50">
                        {submitting ? <FaSpinner className="animate-spin" /> : <FaSave />} Save Lodgement
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BasQuarterPanel;
